import { useState, useEffect } from "react";
import { Search, User, UserCheck, UserX, UserPlus, Filter, MoreHorizontal, Download, FileSpreadsheet, FileText, FileIcon, MessageSquare, Ban, ShieldAlert, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { generateExport, getExportWatermarkEnabled, setExportWatermarkEnabled } from "@/utils/exportUtils";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { mockRecentUsers } from "@/data/adminMocks";
import { useAdminDashboardStore } from "@/data/adminDashboardStore";
import { moroccoRegions, allPartiesIncludingNonParliamentary } from "@/data/parties";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export default function AdminUsersPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [partyFilter, setPartyFilter] = useState("ALL");

  const [watermarkEnabled, setWatermarkEnabled] = useState(getExportWatermarkEnabled());
  
  const { users, fetchUsers, deleteUser, validateUser, isLoadingUsers } = useAdminDashboardStore();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Local states for new features
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [justifRequestedUsers, setJustifRequestedUsers] = useState<Set<string>>(new Set());
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [messageTargetId, setMessageTargetId] = useState<string | "ALL">("ALL");
  const [messageContent, setMessageContent] = useState("");

  const handleToggleWatermark = (val: boolean) => {
     setExportWatermarkEnabled(val);
     setWatermarkEnabled(val);
     toast.success(val ? "Filigrane Kayn'in activé" : "Filigrane désactivé");
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch = 
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.region.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesRole = roleFilter === "ALL" || u.role === roleFilter;
    const matchesRegion = regionFilter === "ALL" || u.region === regionFilter;
    const matchesParty = partyFilter === "ALL" || (u.partyAcronym && u.partyAcronym === partyFilter);

    return matchesSearch && matchesRole && matchesRegion && matchesParty;
  });

  const exportData = (format: 'excel' | 'word' | 'pdf') => {
    toast.success(`Export ${format.toUpperCase()} en cours de génération...`);
    const dataToExport = filteredUsers.map(u => ({
      "ID": u.id,
      "Nom": u.name,
      "Email": u.email,
      "Role": u.role,
      "Acronyme Parti": u.partyAcronym || '-',
      "Region": u.region,
      "Profession": u.profession || u.qualite || '-',
      "Date Inscription": new Date(u.joinDate).toLocaleDateString('fr-FR')
    }));

    generateExport(dataToExport, "Utilisateurs", "Liste des Utilisateurs", format);
  };

  const handleValidateUser = async (id: string, name: string) => {
    const success = await validateUser(id, 'approved');
    if (success) {
      toast.success(`Le compte de ${name} a été validé.`);
    }
  };

  const handleRejectUser = async (id: string, name: string) => {
    const success = await validateUser(id, 'rejected');
    if (success) {
      toast.success(`Le compte de ${name} a été rejeté.`);
    }
  };
  const handleDeleteUser = async (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      const success = await deleteUser(id);
      if (success) {
        const newSelected = new Set(selectedUsers);
        newSelected.delete(id);
        setSelectedUsers(newSelected);
        toast.success("Utilisateur supprimé avec succès.");
      }
    }
  };

  const deleteSelectedUsers = async () => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer les ${selectedUsers.size} utilisateurs sélectionnés ?`)) {
      for (const id of Array.from(selectedUsers)) {
        await deleteUser(id);
      }
      setSelectedUsers(new Set());
      toast.success(`${selectedUsers.size} utilisateurs supprimés.`);
    }
  };

  const resetAllUsers = () => {
    if (confirm("ATTENTION : Voulez-vous vraiment supprimer TOUS les utilisateurs pour réinitialiser la base avant production ? Cette action est irréversible.")) {
      toast.success("La suppression en masse n'est pas encore implémentée via l'interface. Veuillez utiliser la base de données.");
    }
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const toggleSelectUser = (id: string) => {
    const newSet = new Set(selectedUsers);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedUsers(newSet);
  };

  const openMessageDialog = (target: string | "ALL") => {
    setMessageTargetId(target);
    setMessageContent("");
    setIsMessageDialogOpen(true);
  };

  const sendMessage = () => {
    if (!messageContent.trim()) {
      toast.error("Veuillez saisir un message.");
      return;
    }
    
    // Simulation of sending a message
    if (messageTargetId === "ALL") {
      toast.success("Message envoyé à tous les bénéficiaires d'Espaces Dédiés. Vous serez notifié des lectures.");
    } else {
      const user = users.find(u => u.id === messageTargetId);
      
      const newJustif = new Set(justifRequestedUsers);
      newJustif.add(messageTargetId);
      setJustifRequestedUsers(newJustif);
      
      toast.success(`Demande de justificatif envoyée à ${user?.name}.`);
    }
    
    setIsMessageDialogOpen(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion des Inscrits</h1>
          <p className="text-muted-foreground font-body mt-1">
            Consultez et gérez l'ensemble des statuts : grand public, partis, ONG, institutions et médias.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedUsers.size > 0 && (
            <Button variant="destructive" onClick={deleteSelectedUsers} className="gap-2 font-bold">
              <Trash2 className="w-4 h-4" /> Supprimer ({selectedUsers.size})
            </Button>
          )}
          <Button variant="outline" onClick={resetAllUsers} className="gap-2 font-bold text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="w-4 h-4" /> Remise à Zéro
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 font-bold bg-background">
                <Download className="w-4 h-4" /> Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 z-10">
              <DropdownMenuItem onClick={() => exportData('excel')} className="cursor-pointer font-body">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Excel (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData('word')} className="cursor-pointer font-body">
                <FileText className="w-4 h-4 mr-2 text-blue-600" /> Word (.doc)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData('pdf')} className="cursor-pointer font-body">
                <FileIcon className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card className="border-border/50 shadow-sm overflow-hidden">
        <div className="p-4 bg-muted/20 border-b border-border grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative col-span-1 md:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input 
              type="text" 
              placeholder="Nom, email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl border border-input bg-background outline-none focus:ring-2 focus:ring-primary/20 text-sm font-body"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full h-10 rounded-xl bg-background border-input">
              <SelectValue placeholder="Rôle" />
            </SelectTrigger>
            <SelectContent className="rounded-xl max-h-[300px]">
              <SelectItem value="ALL">Tous les statuts</SelectItem>
              <SelectItem value="CITIZEN">Citoyens (Grand public)</SelectItem>
              <SelectItem value="PARTY">Partis</SelectItem>
              <SelectItem value="ONG">ONG</SelectItem>
              <SelectItem value="INSTITUTION">Institutions</SelectItem>
              <SelectItem value="MEDIA">Médias</SelectItem>
            </SelectContent>
          </Select>
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-full h-10 rounded-xl bg-background border-input">
              <SelectValue placeholder="Région" />
            </SelectTrigger>
            <SelectContent className="rounded-xl max-h-[300px]">
              <SelectItem value="ALL">Toutes les régions</SelectItem>
              {moroccoRegions.map((region, idx) => (
                <SelectItem key={idx} value={region.name}>{region.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={partyFilter} onValueChange={setPartyFilter} disabled={roleFilter !== "ALL" && roleFilter !== "PARTY" && roleFilter !== "CITIZEN"}>
            <SelectTrigger className="w-full h-10 rounded-xl bg-background border-input">
              <SelectValue placeholder="Parti" />
            </SelectTrigger>
            <SelectContent className="rounded-xl max-h-[300px]">
              <SelectItem value="ALL">Tous les partis</SelectItem>
              {allPartiesIncludingNonParliamentary.map((party) => (
                <SelectItem key={party.acronym} value={party.acronym}>{party.acronym}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/40 font-semibold font-body border-b border-border">
              <tr>
                <th className="px-6 py-4 rounded-tl-lg w-12">
                  <Checkbox 
                    checked={filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Sélectionner tout"
                  />
                </th>
                <th className="px-6 py-4">Utilisateur</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4">Détails (Région, Métier)</th>
                <th className="px-6 py-4">État (Espace)</th>
                <th className="px-6 py-4">Inscription</th>
                <th className="px-6 py-4 text-right rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const isSelected = selectedUsers.has(user.id);
                  return (
                  <tr key={user.id} className={`border-b border-border/50 hover:bg-muted/10 transition-colors font-body group ${user.accreditationStatus === 'rejected' ? 'opacity-70' : ''} ${isSelected ? 'bg-primary/5' : ''}`}>
                    <td className="px-6 py-4">
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleSelectUser(user.id)}
                        aria-label={`Sélectionner ${user.name}`}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          user.role === 'CITIZEN' ? 'bg-primary/10 text-primary' : 
                          user.role === 'ONG' ? 'bg-accent/10 text-accent' : 
                          'bg-muted-foreground/10 text-foreground'
                        }`}>
                          {user.accreditationStatus === 'rejected' ? <Ban className="w-5 h-5 text-red-500" /> : <User className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-bold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
                            {user.name}
                            {user.accreditationStatus === 'rejected' && <Badge variant="destructive" className="text-[10px] py-0">Rejeté</Badge>}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={`
                        ${user.role === 'CITIZEN' ? 'bg-primary/5 text-primary border-primary/20' : ''}
                        ${user.role === 'ONG' ? 'bg-accent/5 text-accent border-accent/20' : ''}
                        ${user.role === 'PARTY' ? 'bg-background text-foreground border-border' : ''}
                        ${user.role === 'INSTITUTION' ? 'bg-blue-50 text-blue-700 border-blue-200' : ''}
                        ${user.role === 'MEDIA' ? 'bg-purple-50 text-purple-700 border-purple-200' : ''}
                      `}>
                        {user.role === 'CITIZEN' ? 'Citoyen' : user.role === 'ONG' ? 'ONG' : user.role === 'INSTITUTION' ? 'Institution' : user.role === 'MEDIA' ? 'Média' : user.partyAcronym}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <p className="font-medium text-foreground">{user.region}</p>
                      <p className="text-muted-foreground truncate max-w-[200px]">
                        {user.profession || user.qualite} {user.champsActivite ? `(${user.champsActivite})` : ''}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'CITIZEN' ? (
                        <span className="text-muted-foreground italic text-xs">N/A</span>
                      ) : (
                        user.accreditationStatus === 'rejected' ? (
                          <Badge variant="destructive">Rejeté</Badge>
                        ) : user.accreditationStatus === 'approved' ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Validé</Badge>
                        ) : justifRequestedUsers.has(user.id) ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Justificatif demandé</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">En attente</Badge>
                        )
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(user.joinDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-8 h-8 p-0 text-muted-foreground hover:text-foreground">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 z-10">
                          {user.role !== 'CITIZEN' && (
                            <>
                              {user.accreditationStatus !== 'approved' && (
                                <DropdownMenuItem onClick={() => handleValidateUser(user.id, user.name)} className="cursor-pointer font-body text-emerald-600 focus:text-emerald-700 focus:bg-emerald-50">
                                  <UserCheck className="w-4 h-4 mr-2" /> Valider
                                </DropdownMenuItem>
                              )}
                              {user.accreditationStatus !== 'rejected' && (
                                <DropdownMenuItem onClick={() => handleRejectUser(user.id, user.name)} className="cursor-pointer font-body text-red-600 focus:text-red-700 focus:bg-red-50">
                                  <UserX className="w-4 h-4 mr-2" /> Rejeter
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => openMessageDialog(user.id)} className="cursor-pointer font-body text-blue-600 focus:text-blue-700 focus:bg-blue-50">
                                <FileText className="w-4 h-4 mr-2" /> Demander Justificatif
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleDeleteUser(user.id)} className="cursor-pointer font-body focus:bg-muted">
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                )})
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground font-body">
                    Aucun utilisateur trouvé correspondant à ces filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-border flex justify-between items-center text-xs text-muted-foreground font-body">
          <p>Affichage de {filteredUsers.length} comptes simulés.</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-8" disabled>Précédent</Button>
            <Button variant="outline" size="sm" className="h-8">1</Button>
            <Button variant="outline" size="sm" className="h-8">2</Button>
            <Button variant="outline" size="sm" className="h-8" disabled>Suivant</Button>
          </div>
        </div>
      </Card>

      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Message Administratif</DialogTitle>
            <DialogDescription>
              {messageTargetId === "ALL" 
                ? "Ce message sera envoyé à TOUS les utilisateurs ayant un Espace Dédié (Partis, ONG, Médias). Il s'affichera sous forme de notification bloquante avec accusé de lecture." 
                : `Envoyer un message spécifique à ${users.find(u => u.id === messageTargetId)?.name}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contenu du message</Label>
              <Textarea 
                placeholder="Ex: Merci de mettre à jour vos documents d'accréditation avant le 30 mai..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            <div className="bg-amber-50 text-amber-800 p-3 rounded-lg text-xs flex gap-2">
              <span className="text-lg leading-none">ℹ️</span>
              <p>Une fois lu, vous recevrez automatiquement une notification dans votre tableau de bord listant les utilisateurs ayant pris connaissance du message.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)}>Annuler</Button>
            <Button onClick={sendMessage}>Envoyer le message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
